import React, { ReactNode } from "react";
import { Link } from "react-router-dom";

export type NamKazhagamPolicy = "privacy" | "terms";

interface PolicyDocument {
  title: string;
  path: string;
  sections: { id: string; title: string; content: ReactNode }[];
}

const penIndiaContact = {
  organisation: "Populus Empowerment Network (PEN)",
  email: "contact@thepenindia.com",
  address: "Oliver Rd, Kattukoil Garden, Mylapore, 22. First Floor, Chennai, Tamil Nadu, 600004",
};

export const PenIndiaContactDetails = () => (
  <>
    <p>Organisation: <strong>{penIndiaContact.organisation}</strong></p>
    <p>Email: <a href={`mailto:${penIndiaContact.email}`}>{penIndiaContact.email}</a></p>
    <p>Address: {penIndiaContact.address}</p>
  </>
);

export const namKazhagamPolicies: Record<NamKazhagamPolicy, PolicyDocument> = {
  privacy: {
    title: "Privacy Policy",
    path: "/nam-kazhagam/privacy-policy",
    sections: [
      {
        id: "privacy-scope",
        title: "What this policy covers",
        content: <>
          <p>This policy describes how {penIndiaContact.organisation} handles personal information through Nam Kazhagam and its public pages at www.thepenindia.com. It applies to prospective and registered members, event participants, nominees, voters, and people who contact support. Separate notices may apply to particular elections, events, or data collections.</p>
        </>,
      },
      {
        id: "privacy-information",
        title: "Information the Services may handle",
        content: <>
          <p>Depending on your use and the features enabled in the deployed app, information may include:</p>
          <ul>
            <li><strong>Identity and membership:</strong> name, member ID, phone number, optional email, membership source, status and role, and account identifiers.</li>
            <li><strong>Voter-card and electoral-roll data:</strong> EPIC/voter ID number, card images used during scanning, OCR-extracted details, and matches from electoral-roll data uploaded by authorised staff. These may include age or date of birth, gender, address, house number, constituency, booth and serial references, and related location fields.</li>
            <li><strong>Profile media:</strong> a photograph selected from your device or a portrait extracted from a card image. The app may use on-device OCR and face detection to read the card and locate a portrait.</li>
            <li><strong>Location and organisation:</strong> district, constituency, union/town, ward/panchayat, hamlet/street, and membership organisational unit.</li>
            <li><strong>Participation and elections:</strong> event views or participation as implemented, attendance scans and timestamps, points, nominations, proposer/seconder details, eligibility and participation records, and ballot choices/results. Separate storage of member vote receipts and ballot-choice records is not a guarantee of absolute anonymity.</li>
            <li><strong>Communications and administration:</strong> notices delivered or available to you, support requests, administrative changes, role/status history, and audit records.</li>
            <li><strong>Device and technical data:</strong> information needed for sign-in, session storage, app operation, and security.</li>
          </ul>
          <p>Information may come from you, documents you scan, authorised staff, or electoral-roll sources. For questions about the source of your information or an electoral-roll match, contact us using the details below.</p>
        </>,
      },
      {
        id: "privacy-purposes",
        title: "Why information is used",
        content: <>
          <p>Subject to applicable law, information may be used to:</p>
          <ul>
            <li>Verify a phone number and establish or secure an account.</li>
            <li>Register and administer membership, issue a member ID/card, and display a profile photograph.</li>
            <li>Verify or prefill membership details using voter-card or electoral-roll information.</li>
            <li>Assign organisational units and administer membership eligibility and status.</li>
            <li>Provide event information, record attendance, and administer participation points.</li>
            <li>Administer nominations and internal elections, prevent duplicate voting, and publish results.</li>
            <li>Deliver organisational notices to the intended audience.</li>
            <li>Maintain operational, security, audit, and fraud-prevention records.</li>
            <li>Respond to support requests and meet legal obligations.</li>
            <li>Improve or secure the Services only as permitted and described in this policy.</li>
          </ul>
          <p>Member, voter, attendance, or election data must not be used for advertising, external political profiling, or unrelated purposes unless a separate lawful basis, notice, and any required permission have been established.</p>
        </>,
      },
      {
        id: "privacy-choices",
        title: "Legal grounds, notice and choices",
        content: <>
          <p>Processing is subject to applicable legal requirements. Where consent is required, it must be specific to the relevant purpose and may be withdrawn as permitted by law. A general party declaration does not by itself constitute consent to every processing purpose.</p>
          <p>You may decline optional fields or permissions. Some information may be necessary for registration or particular features, and declining it may prevent use of those features.</p>
          <p>For profile corrections or concerns about electoral-roll matches, contact <a href={`mailto:${penIndiaContact.email}`}>{penIndiaContact.email}</a>.</p>
        </>,
      },
      {
        id: "privacy-sharing",
        title: "Sharing and service providers",
        content: <>
          <p>Information may be accessible to authorised organisation staff and providers who need it to operate the Services, including identity/OTP, hosting/database, cloud-storage, messaging, security, and support providers. Information may also be disclosed where required by law or necessary to protect people, rights, or the Services.</p>
          <p>For questions about service providers, processing locations, or cross-border handling of your information, contact us using the details below.</p>
        </>,
      },
      {
        id: "privacy-visibility",
        title: "Public visibility and member directories",
        content: <>
          <p>Some information may be visible to other members or authorised staff depending on role and feature, including through member search, leaderboards, and membership cards.</p>
          <p>Uploaded photographs may be accessible to anyone with their media URL. Do not assume that a member photograph is private or upload content you do not have authority to share.</p>
        </>,
      },
      {
        id: "privacy-retention",
        title: "Retention",
        content: <>
          <p>Information is retained as needed for the purposes described in this policy, membership and internal governance, resolving disputes, legal requirements, and appropriate audit records. Retention depends on the type of record and its purpose, including:</p>
          <ul>
            <li>Incomplete registration and OTP challenge data.</li>
            <li>Member profiles, voter identifiers, and photographs.</li>
            <li>Attendance and participation points.</li>
            <li>Nominations, vote receipts, ballots, and election results.</li>
            <li>Audit/security records, backups, and support records.</li>
          </ul>
          <p>Deletion from an active system does not necessarily mean immediate removal from backups or records subject to lawful retention. For questions about retention of your information, contact us using the details below.</p>
        </>,
      },
      {
        id: "privacy-requests",
        title: "Your requests and account closure",
        content: <>
          <p>Subject to applicable law and required exceptions, you may be able to request access, correction or deletion, withdraw consent where processing relies on it, close an account, or raise a complaint. Identity verification may be required.</p>
          <p>Send requests or enquiries to <a href={`mailto:${penIndiaContact.email}`}>{penIndiaContact.email}</a>. Please describe the information or account concerned and the request you wish to make.</p>
          <p>Account closure and deletion requests may be subject to lawful retention of membership, audit, election, and legal records. Contact us for information about how a request affects your membership and records.</p>
        </>,
      },
      {
        id: "privacy-security",
        title: "Security",
        content: <>
          <p>No internet service can promise absolute security. Protect access to your device and account, and do not share OTPs or other sign-in credentials.</p>
          <p>Report suspected unauthorised access or concerns about your personal information to <a href={`mailto:${penIndiaContact.email}`}>{penIndiaContact.email}</a>.</p>
        </>,
      },
      {
        id: "privacy-children",
        title: "Children",
        content: <>
          <p>Self-registration requires users to be at least 18 years old.</p>
          <p>If you believe a person below the permitted age has registered or provided personal information, contact <a href={`mailto:${penIndiaContact.email}`}>{penIndiaContact.email}</a> so the concern can be addressed in accordance with applicable law.</p>
        </>,
      },
      {
        id: "privacy-cookies",
        title: "Website cookies and analytics",
        content: <>
          <p>These legal pages do not add analytics, advertising pixels, membership forms, or third-party embeds. They use the website's shared resources and hosting.</p>
          <p>For information about data handling across the PEN India website, see the <Link to="/privacy-policy">PEN India website Privacy Policy</Link>.</p>
        </>,
      },
      {
        id: "privacy-contact",
        title: "Changes and contact",
        content: <>
          <p>This policy may change when the Services or applicable requirements change. Updates will be published on this page, with any additional notice required by applicable law.</p>
          <p>For privacy questions, requests, or complaints, use the contact details listed in PEN India's website privacy policy:</p>
          <PenIndiaContactDetails />
          <p>Also read the <Link to="/nam-kazhagam/terms-and-condition">Terms and Conditions</Link>.</p>
        </>,
      },
    ],
  },
  terms: {
    title: "Terms and Conditions",
    path: "/nam-kazhagam/terms-and-condition",
    sections: [
      {
        id: "terms-scope",
        title: "Agreement and scope",
        content: <>
          <p>These Terms of Use cover the Nam Kazhagam mobile application, related membership services, and its public pages at www.thepenindia.com (the "Services"), operated by {penIndiaContact.organisation} (the "Organisation", "we", or "us").</p>
          <p>By using the Services, you agree to these Terms. Please also read the <Link to="/nam-kazhagam/privacy-policy">Privacy Policy</Link>. If you do not agree to these Terms, do not use the Services. Additional rules may apply to particular events, elections, or organisational programmes.</p>
        </>,
      },
      {
        id: "terms-eligibility",
        title: "Eligibility and registration",
        content: <>
          <p>To register, you must be at least 18, provide accurate and up-to-date information, and meet the Organisation's membership rules. Registration may request a phone number for OTP verification, voter ID/EPIC information, profile and location details, and a photograph.</p>
          <p>You are responsible for keeping contact details current and protecting access to your device and account. Do not register using another person's identity or submit information without authority.</p>
          <p>Membership approval, status, and access are governed by the Organisation's applicable membership rules. A digital card or account does not guarantee eligibility for a particular event, role, nomination, or election.</p>
        </>,
      },
      {
        id: "terms-conduct",
        title: "Permitted use and member conduct",
        content: <>
          <p>Use the Services only for lawful, authorised membership and organisational activities. You must not:</p>
          <ul>
            <li>Impersonate another person or submit false, misleading, or unauthorised information.</li>
            <li>Misuse another member's personal information, QR code, account, or device.</li>
            <li>Bypass authentication, interfere with the Services, or access staff-only information without permission.</li>
            <li>Upload malicious, unlawful, infringing, threatening, or otherwise prohibited material.</li>
            <li>Violate applicable law or the Organisation's published conduct rules.</li>
          </ul>
          <p>Follow event instructions and separate election rules shown in the app. Do not share a personal QR code if it could allow another person to check in as you.</p>
        </>,
      },
      {
        id: "terms-participation",
        title: "Events, attendance, points and internal elections",
        content: <>
          <p>Event details, eligibility, attendance, points, nomination review, voting periods, and results are administered under the relevant organisational rules. An authorised organiser may record attendance by scanning a member's QR code. Points and eligibility indicators are administrative records, not money or a transferable benefit.</p>
          <p>Ballot privacy depends on the applicable election process and technical design. Separate storage of voter eligibility receipts and ballot choices does not guarantee absolute anonymity.</p>
          <p>Consult the rules for the relevant election for eligibility, deadlines, challenges, recounts, and results. A submitted vote may not be changeable in the app.</p>
        </>,
      },
      {
        id: "terms-content",
        title: "User content and permissions",
        content: <>
          <p>You retain rights you hold in submitted photographs and other content. You authorise the Organisation to use and store that content as needed for membership records, profile/card display, events, elections, and support, subject to the Privacy Policy.</p>
          <p>Do not submit content you do not have the right to provide.</p>
        </>,
      },
      {
        id: "terms-availability",
        title: "Availability and changes",
        content: <>
          <p>The Services may be updated, suspended, or discontinued in whole or part for maintenance, security, operational, or legal reasons. They may depend on network access and third-party services.</p>
        </>,
      },
      {
        id: "terms-termination",
        title: "Suspension and termination",
        content: <>
          <p>Access may be restricted or suspended where reasonably necessary to protect members, enforce these Terms or organisational rules, investigate suspected misuse, or comply with law.</p>
          <p>Membership decisions and any available review or appeal process are governed by the Organisation's applicable membership rules. For account-closure enquiries, contact <a href={`mailto:${penIndiaContact.email}`}>{penIndiaContact.email}</a>.</p>
          <p>Account closure does not necessarily remove records subject to lawful retention. See the Privacy Policy for information about retention and requests.</p>
        </>,
      },
      {
        id: "terms-third-parties",
        title: "Third-party services",
        content: <>
          <p>The Services may rely on providers for identity verification, messaging, hosting, cloud storage, or app distribution. Their services may be subject to separate terms.</p>
          <p>See the Privacy Policy for information about sharing and service providers.</p>
        </>,
      },
      {
        id: "terms-liability",
        title: "Disclaimers and liability",
        content: <>
          <p>The Services are provided on an "as available" basis to the extent permitted by applicable law. These Terms do not exclude rights or remedies that cannot lawfully be excluded.</p>
        </>,
      },
      {
        id: "terms-law",
        title: "Governing law and changes to these Terms",
        content: <>
          <p>These Terms are subject to applicable law and mandatory legal protections.</p>
          <p>Changes to these Terms will be published on this page, with any additional notice required by applicable law.</p>
        </>,
      },
      {
        id: "terms-contact",
        title: "Contact",
        content: <>
          <p>For questions or complaints about the Terms, use the contact details listed in PEN India's website privacy policy:</p>
          <PenIndiaContactDetails />
          <p>For data handling and requests, read the <Link to="/nam-kazhagam/privacy-policy">Privacy Policy</Link>.</p>
        </>,
      },
    ],
  },
};
